import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { oauthClientsApi, companiesApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../../components/common';

type ApiClientType = 'DEDICATED' | 'PARTNER';

interface CompanyRef {
  id: string;
  name: string;
  code: string;
}

interface ApiClient {
  id: string;
  clientId: string;
  name: string;
  description?: string | null;
  type: ApiClientType;
  companyId?: string | null;
  scopes: string[];
  isActive: boolean;
  lastUsedAt?: string | null;
  createdAt: string;
  company?: CompanyRef | null;
  allowedCompanies?: { company: CompanyRef }[];
}

interface Scope {
  name: string;
  description: string;
}

interface FormData {
  name: string;
  description: string;
  type: ApiClientType;
  companyId: string;
  allowedCompanyIds: string[];
  scopes: string[];
  isActive: boolean;
}

const emptyForm: FormData = {
  name: '',
  description: '',
  type: 'PARTNER',
  companyId: '',
  allowedCompanyIds: [],
  scopes: [],
  isActive: true,
};

export function OAuthClientsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  // API integration clients are platform-only (SUPERADMIN).
  const isAdmin = user?.role === 'SUPERADMIN';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ApiClient | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<ApiClient | null>(null);
  const [regenTarget, setRegenTarget] = useState<ApiClient | null>(null);
  // Credenciales que solo se muestran una vez (al crear o regenerar secret).
  const [credentials, setCredentials] = useState<
    { title: string; clientId?: string; clientSecret: string } | null
  >(null);

  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: async () => {
      const res = await oauthClientsApi.list();
      return res.data.data as ApiClient[];
    },
  });

  const { data: scopes } = useQuery({
    queryKey: ['oauth-scopes'],
    queryFn: async () => {
      const res = await oauthClientsApi.listScopes();
      return res.data.data as Scope[];
    },
  });

  const { data: companies } = useQuery({
    queryKey: ['companies', 'for-oauth'],
    queryFn: async () => {
      const res = await companiesApi.list({ limit: 100 });
      return res.data.data as CompanyRef[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => oauthClientsApi.create(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] });
      const created = res.data.data as { clientId: string; clientSecret: string };
      closeModal();
      setCredentials({
        title: 'Credenciales del cliente API',
        clientId: created.clientId,
        clientSecret: created.clientSecret,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      oauthClientsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] });
      closeModal();
    },
  });

  const regenMutation = useMutation({
    mutationFn: (id: string) => oauthClientsApi.regenerateSecret(id),
    onSuccess: (res) => {
      const result = res.data.data as { clientSecret: string };
      const target = regenTarget;
      setRegenTarget(null);
      setCredentials({
        title: `Nuevo secret — ${target?.name ?? ''}`,
        clientId: target?.clientId,
        clientSecret: result.clientSecret,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => oauthClientsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] });
      setDeleteTarget(null);
    },
  });

  const clients = clientsData ?? [];

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (client: ApiClient) => {
    setEditing(client);
    setFormData({
      name: client.name,
      description: client.description ?? '',
      type: client.type,
      companyId: client.companyId ?? '',
      allowedCompanyIds: client.allowedCompanies?.map((a) => a.company.id) ?? [],
      scopes: client.scopes,
      isActive: client.isActive,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const toggleScope = (scope: string) => {
    setFormData((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const toggleAllowedCompany = (companyId: string) => {
    setFormData((prev) => ({
      ...prev,
      allowedCompanyIds: prev.allowedCompanyIds.includes(companyId)
        ? prev.allowedCompanyIds.filter((c) => c !== companyId)
        : [...prev.allowedCompanyIds, companyId],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      // El backend solo permite actualizar name/description/scopes/isActive.
      updateMutation.mutate({
        id: editing.id,
        data: {
          name: formData.name,
          description: formData.description || undefined,
          scopes: formData.scopes,
          isActive: formData.isActive,
        },
      });
      return;
    }

    const payload: Record<string, unknown> = {
      name: formData.name,
      description: formData.description || undefined,
      type: formData.type,
      scopes: formData.scopes,
    };
    if (formData.type === 'DEDICATED') {
      if (formData.companyId) payload.companyId = formData.companyId;
    } else {
      payload.allowedCompanyIds = formData.allowedCompanyIds;
    }
    createMutation.mutate(payload);
  };

  const copy = (value: string) => {
    void navigator.clipboard?.writeText(value);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integraciones (API Clients)</h1>
          <p className="text-gray-600">
            Credenciales OAuth2 para sistemas externos como GyleERP. Un cliente PARTNER puede
            provisionar empresas y sincronizar dispositivos.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
          >
            Nuevo cliente API
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-10">Cargando...</div>
      ) : clients.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500 text-center py-10">No hay clientes API registrados</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Nombre', 'Client ID', 'Tipo', 'Empresas', 'Scopes', 'Estado', 'Último uso'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  )
                )}
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clients.map((client) => (
                <tr key={client.id}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {client.name}
                    {client.description && (
                      <p className="text-xs text-gray-400 font-normal">{client.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                      {client.clientId}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        client.type === 'PARTNER'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {client.type}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">
                    {client.type === 'DEDICATED'
                      ? client.company?.code ?? '—'
                      : client.allowedCompanies && client.allowedCompanies.length > 0
                        ? client.allowedCompanies.map((a) => a.company.code).join(', ')
                        : 'Todas'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">{client.scopes.length}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        client.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {client.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {client.lastUsedAt
                      ? format(new Date(client.lastUsedAt), 'dd/MM/yyyy HH:mm')
                      : 'Nunca'}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openEdit(client)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setRegenTarget(client)}
                        className="text-amber-600 hover:text-amber-900 mr-3"
                      >
                        Regenerar secret
                      </button>
                      <button
                        onClick={() => setDeleteTarget(client)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editing ? 'Editar cliente API' : 'Nuevo cliente API'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Tipo y empresas solo se definen al crear (inmutables en backend) */}
              {!editing && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value as ApiClientType })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="PARTNER">
                        PARTNER — provisiona varias empresas (GyleERP)
                      </option>
                      <option value="DEDICATED">DEDICATED — atado a una sola empresa</option>
                    </select>
                  </div>

                  {formData.type === 'DEDICATED' ? (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Empresa
                      </label>
                      <select
                        value={formData.companyId}
                        onChange={(e) =>
                          setFormData({ ...formData, companyId: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Selecciona empresa —</option>
                        {(companies ?? []).map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.code} — {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Empresas permitidas{' '}
                        <span className="text-gray-400 font-normal">
                          (vacío = puede provisionar libremente)
                        </span>
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                        {(companies ?? []).map((c) => (
                          <label key={c.id} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={formData.allowedCompanyIds.includes(c.id)}
                              onChange={() => toggleAllowedCompany(c.id)}
                              className="mr-2"
                            />
                            {c.code} — {c.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Scopes</label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                  {(scopes ?? []).map((scope) => (
                    <label key={scope.name} className="flex items-start text-sm">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(scope.name)}
                        onChange={() => toggleScope(scope.name)}
                        className="mr-2 mt-1"
                      />
                      <span>
                        <span className="font-mono text-xs bg-gray-100 px-1 rounded">
                          {scope.name}
                        </span>{' '}
                        <span className="text-gray-500">{scope.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {editing && (
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Activo</span>
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editing ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de credenciales (se muestra una sola vez) */}
      {credentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-2">{credentials.title}</h2>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
              ⚠️ Guarda el <strong>Client Secret</strong> ahora. No se volverá a mostrar.
            </p>

            {credentials.clientId && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Client ID</label>
                <div className="flex gap-2">
                  <code className="flex-1 text-sm font-mono bg-gray-100 px-3 py-2 rounded break-all">
                    {credentials.clientId}
                  </code>
                  <button
                    onClick={() => copy(credentials.clientId!)}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                  >
                    Copiar
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Client Secret</label>
              <div className="flex gap-2">
                <code className="flex-1 text-sm font-mono bg-gray-100 px-3 py-2 rounded break-all">
                  {credentials.clientSecret}
                </code>
                <button
                  onClick={() => copy(credentials.clientSecret)}
                  className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                >
                  Copiar
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setCredentials(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Ya lo guardé
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!regenTarget}
        title="Regenerar client secret"
        message={`Se generará un nuevo secret para "${regenTarget?.name}" y se revocarán todos los tokens activos. ¿Continuar?`}
        confirmLabel="Regenerar"
        destructive
        isLoading={regenMutation.isPending}
        onConfirm={() => regenTarget && regenMutation.mutate(regenTarget.id)}
        onClose={() => setRegenTarget(null)}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Eliminar cliente API"
        message={`¿Seguro de eliminar "${deleteTarget?.name}"? Las integraciones que usen estas credenciales dejarán de funcionar.`}
        confirmLabel="Eliminar"
        destructive
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
